import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { WalletCards, Building2, CreditCard, TrendingUp, ArrowDownCircle, ArrowUpCircle, Users, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { UpgradeRequest } from '../../types/upgrade';

type Props = {
  requests: UpgradeRequest[];
  userRole: string;
  userId: string;
  organizers: Record<string, any>;
};

export const DeductionStatsPanel = ({ requests, userRole, userId, organizers }: Props) => {
  const { translations } = useLanguage();

  // Calculate deduction statistics based on user role
  const stats = useMemo(() => {
    // For organizer, show only their own deductions
    let relevantRequests = requests;
    if (userRole === 'organizer') {
      relevantRequests = requests.filter(req => req.organizerId === userId);
    }

    // Total amount and counts by status and organizer
    const totalDeducted = relevantRequests
      .filter(req => req.status === 'paid' || req.status === 'completed')
      .reduce((sum, req) => sum + (req.price || 0), 0);

    const pendingDeductions = relevantRequests
      .filter(req => req.status === 'approved')
      .reduce((sum, req) => sum + (req.price || 0), 0);

    const completedUpgrades = relevantRequests
      .filter(req => req.status === 'completed')
      .length;

    const pendingApproval = relevantRequests
      .filter(req => req.status === 'pending')
      .length;
      
    // New stat: Confirmed deducted (completed upgrades amount)
    const confirmedDeducted = relevantRequests
      .filter(req => req.status === 'completed')
      .reduce((sum, req) => sum + (req.price || 0), 0);

    // For admin, calculate per-organizer stats
    const organizerStats: Record<string, {
      organizerId: string,
      organizerName: string,
      totalDeducted: number,
      pendingDeductions: number,
      completedUpgrades: number,
      confirmedDeducted: number
    }> = {};

    if (userRole === 'admin') {
      relevantRequests.forEach(req => {
        if (!organizerStats[req.organizerId]) {
          organizerStats[req.organizerId] = {
            organizerId: req.organizerId,
            organizerName: req.organizerName,
            totalDeducted: 0,
            pendingDeductions: 0,
            completedUpgrades: 0,
            confirmedDeducted: 0
          };
        }

        if (req.status === 'paid' || req.status === 'completed') {
          organizerStats[req.organizerId].totalDeducted += (req.price || 0);
        }

        if (req.status === 'approved') {
          organizerStats[req.organizerId].pendingDeductions += (req.price || 0);
        }

        if (req.status === 'completed') {
          organizerStats[req.organizerId].completedUpgrades += 1;
          organizerStats[req.organizerId].confirmedDeducted += (req.price || 0);
        }
      });
    }

    return {
      totalDeducted,
      pendingDeductions,
      completedUpgrades,
      pendingApproval,
      confirmedDeducted,
      organizerStats: Object.values(organizerStats).sort((a, b) => b.totalDeducted - a.totalDeducted)
    };
  }, [requests, userRole, userId]);

  return (
    <div className="space-y-6">
      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-50 rounded-lg">
              <CreditCard className="text-red-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Deducted</p>
              <p className="text-2xl font-bold text-red-600">{stats.totalDeducted.toLocaleString()} SAR</p>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            For all completed and paid upgrades
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-50 rounded-lg">
              <WalletCards className="text-yellow-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending Deductions</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingDeductions.toLocaleString()} SAR</p>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Awaiting balance deduction confirmation
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-50 rounded-lg">
              <CheckCircle className="text-green-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Confirmed Deducted</p>
              <p className="text-2xl font-bold text-green-600">{stats.confirmedDeducted.toLocaleString()} SAR</p>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Confirmed and completed upgrades
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-50 rounded-lg">
              <ArrowUpCircle className="text-green-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Completed Upgrades</p>
              <p className="text-2xl font-bold text-green-600">{stats.completedUpgrades.toLocaleString()}</p>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Successfully processed upgrades
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <TrendingUp className="text-blue-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending Approval</p>
              <p className="text-2xl font-bold text-blue-600">{stats.pendingApproval.toLocaleString()}</p>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Awaiting admin approval
          </div>
        </motion.div>
      </div>

      {/* Admin Only: Organizer Breakdown */}
      {userRole === 'admin' && stats.organizerStats.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Users className="text-primary-500" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Organizers Deduction Stats</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">Organizer</th>
                  <th className="px-4 py-3 text-right">Total Deducted</th>
                  <th className="px-4 py-3 text-right">Confirmed Deducted</th>
                  <th className="px-4 py-3 text-right">Pending Deductions</th>
                  <th className="px-4 py-3 text-right">Completed Upgrades</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.organizerStats.map((org, index) => (
                  <tr key={org.organizerId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 font-medium text-gray-900">{org.organizerName}</td>
                    <td className="px-4 py-3 text-right font-medium text-red-600">{org.totalDeducted.toLocaleString()} SAR</td>
                    <td className="px-4 py-3 text-right font-medium text-green-600">{org.confirmedDeducted.toLocaleString()} SAR</td>
                    <td className="px-4 py-3 text-right text-yellow-600">{org.pendingDeductions.toLocaleString()} SAR</td>
                    <td className="px-4 py-3 text-right text-green-600">{org.completedUpgrades}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 font-semibold text-gray-900">
                <tr>
                  <td className="px-4 py-3">Total</td>
                  <td className="px-4 py-3 text-right text-red-600">{stats.totalDeducted.toLocaleString()} SAR</td>
                  <td className="px-4 py-3 text-right text-green-600">{stats.confirmedDeducted.toLocaleString()} SAR</td>
                  <td className="px-4 py-3 text-right text-yellow-600">{stats.pendingDeductions.toLocaleString()} SAR</td>
                  <td className="px-4 py-3 text-right text-green-600">{stats.completedUpgrades}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </motion.div>
      )}

      {/* Organizer View - More Detailed Breakdown */}
      {userRole === 'organizer' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-50 rounded-lg">
              <WalletCards className="text-red-500" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Deduction Overview</h3>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Deduction Breakdown */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-gray-700">Deduction Breakdown</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Completed Upgrades</span>
                    <span className="font-medium text-green-600">
                      {stats.completedUpgrades} × Average {stats.completedUpgrades > 0 ? Math.round(stats.confirmedDeducted / stats.completedUpgrades) : 0} SAR
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Confirmed Deducted</span>
                    <span className="font-bold text-green-600">{stats.confirmedDeducted.toLocaleString()} SAR</span>
                  </div>
                  <div className="h-px bg-gray-200 my-2"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Amount Deducted</span>
                    <span className="font-bold text-red-600">{stats.totalDeducted.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Pending Deductions</span>
                    <span className="font-medium text-yellow-600">{stats.pendingDeductions.toLocaleString()} SAR</span>
                  </div>
                </div>
              </div>

              {/* Status Distribution */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-gray-700">Status Distribution</h4>
                {/* Count upgrades by status and visualize */}
                {(() => {
                  const statusCounts = {
                    pending: requests.filter(req => req.organizerId === userId && req.status === 'pending').length,
                    approved: requests.filter(req => req.organizerId === userId && req.status === 'approved').length,
                    paid: requests.filter(req => req.organizerId === userId && req.status === 'paid').length,
                    completed: requests.filter(req => req.organizerId === userId && req.status === 'completed').length,
                    rejected: requests.filter(req => req.organizerId === userId && req.status === 'rejected').length,
                  };

                  const total = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);

                  return (
                    <div className="space-y-3">
                      {/* Pending */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Pending</span>
                          <span className="font-medium">{statusCounts.pending} ({total ? Math.round((statusCounts.pending / total) * 100) : 0}%)</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-2 bg-blue-500 rounded-full" 
                            style={{ width: `${total ? (statusCounts.pending / total) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Approved */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Pending Payment</span>
                          <span className="font-medium">{statusCounts.approved} ({total ? Math.round((statusCounts.approved / total) * 100) : 0}%)</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-2 bg-yellow-500 rounded-full" 
                            style={{ width: `${total ? (statusCounts.approved / total) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Paid */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Paid</span>
                          <span className="font-medium">{statusCounts.paid} ({total ? Math.round((statusCounts.paid / total) * 100) : 0}%)</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-2 bg-orange-500 rounded-full" 
                            style={{ width: `${total ? (statusCounts.paid / total) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Completed */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Completed</span>
                          <span className="font-medium">{statusCounts.completed} ({total ? Math.round((statusCounts.completed / total) * 100) : 0}%)</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-2 bg-green-500 rounded-full" 
                            style={{ width: `${total ? (statusCounts.completed / total) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Rejected */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Rejected</span>
                          <span className="font-medium">{statusCounts.rejected} ({total ? Math.round((statusCounts.rejected / total) * 100) : 0}%)</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-2 bg-red-500 rounded-full" 
                            style={{ width: `${total ? (statusCounts.rejected / total) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};